import { createSelector } from 'reselect'
import { Decimal } from 'decimal.js'

import { FETCH_STATUSES } from 'resources'

import { getRedemptionMinAccountBalance } from 'models/General/selectors'
import {
  getBalance,
  getHasPrimaryPhoneNumber,
  getPrimaryEmailAddress,
  getPrimaryPhoneNumber,
} from 'models/ProfileInfo/selectors'
import { getGoalReward } from 'models/Rewards/selectors'

import getDecimalValue from 'utils/get-decimal-value'
import { parseToken } from 'utils/jwt'

import { TOKEN_REQUIRED_KEYS } from './constants'

const getTremendousRedemptionOptions = (state) => state.redemptions.tremendousOptions

const getHasNoOpenRedemptions = (state) => !!state.redemptions.statuses.hasNoOpenRedemptions

const getHasMinAccountMaturity = (state) => !!state.redemptions.statuses.hasMinAccountMaturity

const getHasNoRecentRedemptions = (state) => !!state.redemptions.statuses.hasNoRecentRedemptions

const getHasMinAccountBalance = (state) => !!state.redemptions.statuses.hasMinAccountBalance

const getHasPrimaryEmailAddress = (state) => !!state.redemptions.statuses.hasEmail

const getHasNoMultipleAccounts = (state) => !!state.redemptions.statuses.hasNoMultipleAccounts

export const getIsFetchingRedemptionOptionsStatus = (state) =>
  state.redemptions.tremendousFetchStatus === FETCH_STATUSES.FETCHING ||
  state.redemptions.cryptoFetchStatus === FETCH_STATUSES.FETCHING

export const getIsFetchingQuoteToken = (state) =>
  state.redemptions.cryptoQuoteTokenFetchStatus === FETCH_STATUSES.FETCHING

const getRemainingDaysAccountMaturity = (state) => state.redemptions.params.remainingDaysAccountMaturity

export const getRemainingDaysNextRedemption = (state) => state.redemptions.params.remainingDaysNextRedemption

export const getCryptoOptions = (state) => state.redemptions.cryptoOptions

export const getRedemptionOptionsList = createSelector(getCryptoOptions, (cryptoOptions) =>
  Object.keys(cryptoOptions).concat(['giftCard']),
)

export const getCryptoCurrency = (state) => state.redemptions.currency

const getMinRedemptionAmountsForCrypto = createSelector(getCryptoOptions, (cryptoOptions) =>
  Object.values(cryptoOptions)
    .map((value) => value?.minimumAmount ?? null)
    .filter((value) => value !== null),
)

export const getMinRedemptionAmountForCrypto = createSelector(getMinRedemptionAmountsForCrypto, (minAmounts) =>
  minAmounts.length > 0 ? Decimal.min(...minAmounts) : new Decimal(Number.MAX_VALUE),
)

export const getHasMinRedemptionBalance = createSelector(
  [getRedemptionMinAccountBalance, getBalance],
  (redemptionMinAccountBalance, userBalance) => getDecimalValue(userBalance).gte(redemptionMinAccountBalance),
)

export const getRedemptionRequirements = createSelector(
  getHasMinAccountMaturity,
  getHasNoOpenRedemptions,
  getHasNoRecentRedemptions,
  getHasNoMultipleAccounts,
  getGoalReward,
  getHasPrimaryEmailAddress,
  getHasPrimaryPhoneNumber,
  (
    hasMinAccountMaturity,
    hasNoOpenRedemptions,
    hasNoRecentRedemptions,
    hasNoMultipleAccounts,
    goalReward,
    hasPrimaryEmail,
    hasPrimaryPhone,
  ) => {
    const baseRequirements = {
      hasMinAccountMaturity,
      hasNoOpenRedemptions,
      hasNoRecentRedemptions,
      hasNoMultipleAccounts,
    }
    if (goalReward?.id === 'venmo') {
      return {
        ...baseRequirements,
        hasPrimaryPhone,
      }
    }
    if (goalReward?.id === 'paypal') {
      return {
        ...baseRequirements,
        hasPrimaryEmail,
      }
    }
    return {
      ...baseRequirements,
    }
  },
)

export const getIsRequirementMissing = createSelector(getRedemptionRequirements, (requirements) =>
  Object.values(requirements).some((v) => !v),
)

export const getCanUserRedeem = createSelector(
  [getHasMinRedemptionBalance, getIsRequirementMissing, getGoalReward, getPrimaryEmailAddress, getPrimaryPhoneNumber],
  (hasMinRedemptionBalance, isRequirementMissing, goalReward, primaryEmailAddress, primaryPhoneNumber) => {
    const baseRequirement = hasMinRedemptionBalance && !isRequirementMissing
    if (goalReward?.id === 'paypal') {
      return baseRequirement && !!primaryEmailAddress
    }
    if (goalReward?.id === 'venmo') {
      return baseRequirement && !!primaryPhoneNumber
    }
    if (goalReward?.id === 'shares') {
      return !isRequirementMissing
    }
    return baseRequirement
  },
)

export const getQuoteToken = (state) => state.redemptions.cryptoQuoteToken

export const getParsedToken = createSelector(getQuoteToken, (token) => {
  const parsedToken = parseToken(token)
  return Object.keys(parsedToken).length ? parsedToken : null
})

export const getIsTokenValidFormat = createSelector(
  getParsedToken,
  (parsedToken) => parsedToken && TOKEN_REQUIRED_KEYS.every((key) => !!parsedToken[key]),
)

export default {
  getTremendousRedemptionOptions,
  getRedemptionRequirements,
  getIsRequirementMissing,
  getCanUserRedeem,
  getRemainingDaysAccountMaturity,
  getRemainingDaysNextRedemption,
  getHasMinAccountBalance,
  getHasMinAccountMaturity,
  getHasNoOpenRedemptions,
  getHasNoRecentRedemptions,
  getHasNoMultipleAccounts,
  getHasPrimaryEmailAddress,
  getRedemptionOptionsList,
  getCryptoOptions,
  getCryptoCurrency,
  getMinRedemptionAmountForCrypto,
  getIsFetchingRedemptionOptionsStatus,
  getIsFetchingQuoteToken,
}
